import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface MaintenanceType {
    id: string;
    user_id: string;
    name: string;
    interval_km: number;
    icon?: string;
    created_at: string;
}

export const useMaintenanceTypes = () => {
    const { user } = useAuth();
    const [types, setTypes] = useState<MaintenanceType[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            setTypes([]);
            setLoading(false);
            return;
        }

        fetchTypes();
    }, [user]);

    const fetchTypes = async () => {
        try {
            const { data, error } = await supabase
                .from('maintenance_types')
                .select('*')
                .order('created_at', { ascending: true });

            if (error) throw error;
            setTypes(data || []);
        } catch (error) {
            console.error('[useMaintenanceTypes] Error fetching types:', error);
        } finally {
            setLoading(false);
        }
    };

    const addType = async (name: string, intervalKm: number, icon?: string) => {
        if (!user) return;

        try {
            const { data, error } = await supabase
                .from('maintenance_types')
                .insert([{
                    user_id: user.id,
                    name,
                    interval_km: intervalKm,
                    icon
                }])
                .select()
                .single();

            if (error) throw error;

            setTypes(prev => [...prev, data]);
            return data;
        } catch (error) {
            console.error('[useMaintenanceTypes] Error adding type:', error);
            throw error;
        }
    };

    const deleteType = async (id: string) => {
        try {
            const { error } = await supabase
                .from('maintenance_types')
                .delete()
                .eq('id', id);

            if (error) throw error;

            setTypes(prev => prev.filter(t => t.id !== id));
        } catch (error) {
            console.error('[useMaintenanceTypes] Error deleting type:', error);
            throw error;
        }
    };

    return { types, loading, addType, deleteType, refresh: fetchTypes };
};
